import { PdfOrientation, PdfPaperSize } from '../api/types';

/**
 * Métriques de mise en page partagées entre l'aperçu (nombre de pages affiché
 * dans PdfExportSettingsModal) et la génération réelle (pdfExport.ts) : les
 * DEUX utilisent exactement le même calcul (computePagePlan), donc l'aperçu
 * n'est plus une simple estimation approximative — il annonce le nombre de
 * pages RÉEL qui sera produit.
 */

export const DAY_WIDTH = 24;
export const ROW_HEIGHT = 28;
export const MONTH_ROW_HEIGHT = 18;
export const WEEK_ROW_HEIGHT = 16;
export const DAY_ROW_HEIGHT = 18;
export const TIMELINE_HEADER_HEIGHT = MONTH_ROW_HEIGHT + WEEK_ROW_HEIGHT + DAY_ROW_HEIGHT;
export const NUM_COL_WIDTH = 30;

export const COLUMN_WIDTH: Record<string, number> = {
  nameFr: 170,
  nameEn: 170,
  duration: 55,
  start: 78,
  end: 78,
  predecessors: 90,
  progress: 40,
  milestone: 60,
  diagAuto: 60,
};

/** Bandeau nom+logos : taille FIXE en mm, répétée identique sur chaque page, jamais affectée par `scalePercent` (sinon un logo deviendrait illisible sur un export très réduit). */
export const PAGE_HEADER_BAND_MM = 14;
export const PAGE_HEADER_GAP_MM = 2;
export const LOGO_SIZE_MM = 10;

export const PX_TO_MM = 25.4 / 96;

export const PAPER_SIZES_MM: Record<PdfPaperSize, { w: number; h: number }> = {
  A0: { w: 841, h: 1189 },
  A1: { w: 594, h: 841 },
  A2: { w: 420, h: 594 },
  A3: { w: 297, h: 420 },
  A4: { w: 210, h: 297 },
  Letter: { w: 215.9, h: 279.4 },
  Legal: { w: 215.9, h: 355.6 },
};

export function orientedPaperSizeMm(paperSize: PdfPaperSize, orientation: PdfOrientation): { w: number; h: number } {
  const { w, h } = PAPER_SIZES_MM[paperSize];
  return orientation === 'landscape' ? { w: h, h: w } : { w, h };
}

export function labelColWidthPx(columns: string[]): number {
  return NUM_COL_WIDTH + columns.reduce((sum, c) => sum + (COLUMN_WIDTH[c] ?? 70), 0);
}

export interface PagePlanInput {
  taskCount: number;
  dayCount: number;
  columns: string[];
  paperSize: PdfPaperSize;
  orientation: PdfOrientation;
  marginCm: number;
  scalePercent: number;
}

export interface PagePlan {
  pagesWide: number;
  pagesTall: number;
  /** Nombre de lignes de tâches par page (dernière page possiblement partielle). */
  rowsPerPage: number;
  /** Jours affichés sur la 1re colonne de pages (celle qui inclut le tableau) — peut être 0 si le tableau seul dépasse déjà la largeur utile. */
  daysOnFirstCol: number;
  /** Jours affichés sur chaque colonne de pages suivante (frise seule, sans tableau). */
  daysPerLaterCol: number;
  marginMm: number;
  usableWmm: number;
  usableHmm: number;
  /** Zone utile pour le contenu (tableau+frise), en "px" logiques, une fois le bandeau d'en-tête retiré et l'échelle appliquée. */
  contentUsableWpx: number;
  contentUsableHpx: number;
  labelColWidth: number;
}

export function computePagePlan(input: PagePlanInput): PagePlan {
  const paper = orientedPaperSizeMm(input.paperSize, input.orientation);
  const marginMm = input.marginCm * 10;
  const usableWmm = Math.max(paper.w - marginMm * 2, 10);
  const usableHmm = Math.max(paper.h - marginMm * 2, 10);
  const scale = input.scalePercent / 100;

  const contentUsableHmm = Math.max(usableHmm - PAGE_HEADER_BAND_MM - PAGE_HEADER_GAP_MM, 10);
  const contentUsableWpx = usableWmm / (PX_TO_MM * scale);
  const contentUsableHpx = contentUsableHmm / (PX_TO_MM * scale);

  const labelColWidth = labelColWidthPx(input.columns);
  const taskCount = Math.max(input.taskCount, 1);
  const dayCount = Math.max(input.dayCount, 1);

  const rowsAvailablePx = Math.max(contentUsableHpx - TIMELINE_HEADER_HEIGHT, ROW_HEIGHT);
  const rowsPerPage = Math.max(1, Math.floor(rowsAvailablePx / ROW_HEIGHT));
  const pagesTall = Math.max(1, Math.ceil(taskCount / rowsPerPage));

  const daysOnFirstCol = Math.max(0, Math.floor((contentUsableWpx - labelColWidth) / DAY_WIDTH));
  const daysPerLaterCol = Math.max(1, Math.floor(contentUsableWpx / DAY_WIDTH));
  const remainingDays = Math.max(0, dayCount - daysOnFirstCol);
  const pagesWide = daysOnFirstCol >= dayCount ? 1 : 1 + Math.ceil(remainingDays / daysPerLaterCol);

  return {
    pagesWide,
    pagesTall,
    rowsPerPage,
    daysOnFirstCol,
    daysPerLaterCol,
    marginMm,
    usableWmm,
    usableHmm,
    contentUsableWpx,
    contentUsableHpx,
    labelColWidth,
  };
}

/** Pourcentage d'échelle (entier, borné à [10,400] comme la validation serveur) qui ferait tenir tout le contenu sur UNE page, en largeur ET en hauteur. */
export function scaleToFitOnePage(
  taskCount: number,
  dayCount: number,
  columns: string[],
  paperSize: PdfPaperSize,
  orientation: PdfOrientation,
  marginCm: number,
): number {
  const paper = orientedPaperSizeMm(paperSize, orientation);
  const marginMm = marginCm * 10;
  const usableWmm = Math.max(paper.w - marginMm * 2, 10);
  const usableHmm = Math.max(paper.h - marginMm * 2, 10);
  const contentUsableHmm = Math.max(usableHmm - PAGE_HEADER_BAND_MM - PAGE_HEADER_GAP_MM, 10);

  const requiredWpx = labelColWidthPx(columns) + Math.max(dayCount, 1) * DAY_WIDTH;
  const requiredHpx = TIMELINE_HEADER_HEIGHT + Math.max(taskCount, 1) * ROW_HEIGHT;

  const scaleForW = (usableWmm / (requiredWpx * PX_TO_MM)) * 100;
  const scaleForH = (contentUsableHmm / (requiredHpx * PX_TO_MM)) * 100;
  return Math.max(10, Math.min(400, Math.floor(Math.min(scaleForW, scaleForH))));
}
